import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Loader2, Save, Plus, Trash2, Globe, Euro, BellRing } from "lucide-react";

const SURCHARGE_LABELS = [
  { key: "schraegesZaunfeld", label: "Schräges Zaunfeld oder Geländer", unit: "€ / lfm" },
  { key: "tueranschlagGehtuere", label: "Durchgehender Türanschlag / Spaltabdeckung — Gehtüre", unit: "€ / Stk." },
  { key: "tueranschlagDoppelfluegel", label: "Durchgehender Türanschlag / Spaltabdeckung — Doppelflügeltor", unit: "€ / Stk." },
  { key: "zylinder", label: "Zylinder", unit: "€ / Stk." },
  { key: "winkelschnitt", label: "Winkelschnitt bei Pfosten", unit: "€ / Stk." },
  { key: "seitenmontageplatte", label: "Seitenmontageplatte direkt Abstand 10 mm", unit: "€ / Stk." },
  { key: "maxiSystemSchiebetor", label: "MAXI-System für Freitragende Schiebetore ab Lichte 5250 mm (Breite > 5250 mm)", unit: "€ / Stk." },
];

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countries, setCountries] = useState([]);
  const [surcharges, setSurcharges] = useState({});
  const [reminder, setReminder] = useState({ enabled: true, days: 7 });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/settings");
        setCountries(r.data.countries || []);
        setSurcharges(r.data.surcharges || {});
        setReminder(r.data.reminder || { enabled: true, days: 7 });
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = {
        countries: countries
          .filter((c) => c.name.trim())
          .map((c) => ({ name: c.name.trim(), mwstPercent: Number(c.mwstPercent) || 0 })),
        surcharges: Object.fromEntries(
          Object.entries(surcharges).map(([k, v]) => [k, Number(v) || 0])
        ),
        reminder: { enabled: !!reminder.enabled, days: Math.min(90, Math.max(1, Number(reminder.days) || 7)) },
      };
      const r = await api.put("/admin/settings", payload);
      setCountries(r.data.countries);
      setSurcharges(r.data.surcharges);
      if (r.data.reminder) setReminder(r.data.reminder);
      setSuccess("Einstellungen gespeichert.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const setCountry = (idx, patch) => setCountries((prev) => {
    const arr = [...prev];
    arr[idx] = { ...arr[idx], ...patch };
    return arr;
  });

  if (loading) return <div className="p-10 text-sm text-zinc-500">Wird geladen...</div>;

  return (
    <div className="p-4 md:p-10 max-w-3xl" data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">Einstellungen</h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save-button"
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Speichern
        </button>
      </div>

      {error && <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4" data-testid="settings-error">{error}</div>}
      {success && <div className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-sm mb-4" data-testid="settings-success">{success}</div>}

      {/* Länder & MwSt */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-200">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 flex items-center gap-2">
            <Globe size={14} /> Länder & MwSt-Sätze
          </h2>
          <button onClick={() => setCountries((p) => [...p, { name: "", mwstPercent: 0 }])}
                  data-testid="add-country-button"
                  className="text-xs text-zinc-600 hover:text-zinc-900 underline flex items-center gap-1">
            <Plus size={12} /> Land hinzufügen
          </button>
        </div>
        <div className="border border-zinc-200">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
            <div className="col-span-7">Land</div>
            <div className="col-span-4">MwSt %</div>
            <div className="col-span-1"></div>
          </div>
          {countries.map((c, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 items-center" data-testid={`country-row-${idx}`}>
              <div className="col-span-7">
                <input value={c.name} onChange={(e) => setCountry(idx, { name: e.target.value })}
                       placeholder="Ländername"
                       data-testid={`country-name-${idx}`}
                       className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black" />
              </div>
              <div className="col-span-4">
                <input type="number" step="0.1" value={c.mwstPercent}
                       onChange={(e) => setCountry(idx, { mwstPercent: e.target.value })}
                       data-testid={`country-mwst-${idx}`}
                       className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black" />
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => setCountries((p) => p.filter((_, i) => i !== idx))}
                        data-testid={`country-delete-${idx}`}
                        className="text-zinc-400 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {countries.length === 0 && (
            <div className="px-3 py-6 text-sm text-zinc-500 text-center">Keine Länder — fügen Sie mindestens ein Land hinzu.</div>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Die MwSt wird im Angebot automatisch anhand des gewählten Landes angewendet (mit manueller Übersteuerung möglich).
        </p>
      </section>

      {/* Zuschläge */}
      <section>
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-200">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 flex items-center gap-2">
            <Euro size={14} /> Zuschläge / Optionen
          </h2>
        </div>
        <div className="border border-zinc-200 divide-y divide-zinc-100">
          {SURCHARGE_LABELS.map(({ key, label, unit }) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-3" data-testid={`surcharge-row-${key}`}>
              <div className="flex-1 text-sm text-zinc-800">{label}</div>
              <div className="flex items-center gap-2">
                <input type="number" step="0.5" value={surcharges[key] ?? ""}
                       onChange={(e) => setSurcharges((p) => ({ ...p, [key]: e.target.value }))}
                       data-testid={`surcharge-input-${key}`}
                       className="w-28 px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono text-right focus:border-black focus:ring-1 focus:ring-black" />
                <span className="text-xs text-zinc-500 w-16">{unit}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Diese Preise werden im Angebotskonfigurator als wählbare Optionen angeboten und automatisch berechnet.
        </p>
      </section>

      <section data-testid="reminder-settings">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-200">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 flex items-center gap-2">
            <BellRing size={14} /> Automatische E-Mail-Erinnerung
          </h2>
        </div>
        <div className="border border-zinc-200 px-3 py-3 flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer">
            <input type="checkbox" checked={!!reminder.enabled} onChange={(e) => setReminder((p) => ({ ...p, enabled: e.target.checked }))}
                   className="accent-black" data-testid="reminder-enabled" />
            Erinnerung senden, wenn ein Angebot nicht geöffnet wurde
          </label>
          <div className="flex items-center gap-2 text-sm text-zinc-700">
            nach
            <input type="number" min={1} max={90} value={reminder.days} onChange={(e) => setReminder((p) => ({ ...p, days: e.target.value }))}
                   data-testid="reminder-days"
                   className="w-20 px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono text-right focus:border-black focus:ring-1 focus:ring-black" />
            Tagen
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Geprüft wird stündlich: Wurde die letzte Angebots-E-Mail weder geöffnet noch der Link angesehen, geht automatisch eine Erinnerung (mit PDF) an dieselben Empfänger. Pro Angebot nur einmal.
        </p>
      </section>
    </div>
  );
}
